// Payroll Summary report — mirrors OnTheClock's Reports → Payroll Summary.
// KPI cards (Regular / OT / Time Off / Total) + per-employee table.

import { useEffect, useState } from 'react';
import { api } from '../../api';

function hours(sec) { return ((sec || 0) / 3600).toFixed(2); }

export default function PayrollReportPage() {
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [accrual, setAccrual] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  function load() {
    if (!periodId) return;
    api.payPeriodSummary(periodId).then(setSummary).catch(e => setErr(e.message));
    api.payrollAccrual(periodId).then(setAccrual).catch(() => setAccrual([]));
    api.listPeriodApprovals(periodId).then(setApprovals).catch(() => setApprovals([]));
  }

  useEffect(() => {
    api.listPayPeriods().then(rows => {
      setPeriods(rows);
      if (rows[0]) setPeriodId(rows[0].id);
    }).catch(e => setErr(e.message));
  }, []);
  useEffect(load, [periodId]);

  async function closePeriod() {
    if (!window.confirm('Close this pay period? PTO will accrue and managers will be notified.')) return;
    setBusy(true); setErr('');
    try { await api.closePayPeriod(periodId, false); load(); }
    catch (e) {
      if (e.responseBody?.stillOpen) {
        if (window.confirm(`${e.responseBody.stillOpen} shift(s) still open. Force close anyway?`)) {
          try { await api.closePayPeriod(periodId, true); load(); } catch (e2) { setErr(e2.message); }
        }
      } else { setErr(e.message); }
    }
    finally { setBusy(false); }
  }

  async function managerApprove() {
    const note = window.prompt('Approval note (optional):') || '';
    setBusy(true);
    try { const r = await api.managerApprovePeriod(periodId, note); load();
      if (r.all_complete) alert('All managers signed off. CEO has been notified.');
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }
  async function managerReject() {
    const note = window.prompt('Reason for sending back (required):');
    if (!note) return;
    setBusy(true);
    try { await api.managerRejectPeriod(periodId, note); load(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }
  async function ceoFinalize() {
    if (!window.confirm('Finalize this pay period as CEO? Marks as paid.')) return;
    setBusy(true);
    try { await api.finalizePayPeriod(periodId, ''); load(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  function download() {
    const url = api.exportPayrollCsv(periodId);
    const token = localStorage.getItem('token');
    const pinToken = localStorage.getItem('pinSession');
    fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        ...(pinToken ? { 'X-Pin-Session': pinToken } : {}),
      },
    }).then(r => r.blob()).then(blob => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `payroll-period-${periodId}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    });
  }

  // KPI totals
  const totals = summary?.summaries?.reduce((acc, s) => {
    acc.regular += s.regular_seconds;
    acc.ot += s.ot_seconds;
    acc.pto += s.pto_seconds;
    return acc;
  }, { regular: 0, ot: 0, pto: 0 }) || { regular: 0, ot: 0, pto: 0 };
  const total = totals.regular + totals.ot + totals.pto;

  const period = periods.find(p => p.id === periodId);

  return (
    <div className="page">
      <div className="page-header">
        <h2>Payroll Summary</h2>
        <div className="filter-bar">
          <select value={periodId || ''} onChange={e => setPeriodId(parseInt(e.target.value, 10))}>
            {periods.map(p => <option key={p.id} value={p.id}>{p.start_date} → {p.end_date} ({p.status})</option>)}
          </select>
          <button className="btn btn-gold" onClick={download} disabled={!periodId}>Export CSV</button>
        </div>
      </div>
      {err && <div className="error-banner">{err}</div>}
      {period && (
        <div className="muted" style={{ marginBottom: 12 }}>
          Pay Period: <strong>{period.start_date} thru {period.end_date}</strong> · Status: <span className={`status-pill status-${period.status}`}>{period.status}</span>
        </div>
      )}

      {summary && period && (
        <div className="period-actions">
          {/* Open period: any manager can close */}
          {period.status === 'open' && (
            <button className="btn-primary" onClick={closePeriod} disabled={busy}>Close Period (lock + accrue PTO + notify managers)</button>
          )}
          {/* Locked period: manager can approve/reject their slice */}
          {period.status === 'locked' && !summary.viewer_is_ceo && summary.my_approval === 'pending' && (
            <>
              <button className="btn-primary" onClick={managerApprove} disabled={busy}>Approve My Team's Timecards</button>
              <button className="btn-secondary" onClick={managerReject} disabled={busy}>Reject (send back for fixes)</button>
            </>
          )}
          {period.status === 'locked' && summary.my_approval === 'approved' && (
            <div className="muted">You've approved your team's timecards. Waiting on other managers.</div>
          )}
          {period.status === 'locked' && summary.viewer_is_ceo && (
            <div className="muted">Waiting on managers to approve. Status below.</div>
          )}
          {/* GM-approved: CEO finalizes */}
          {period.status === 'gm_approved' && summary.viewer_is_ceo && (
            <button className="btn-primary" onClick={ceoFinalize} disabled={busy}>Finalize Payroll (mark paid)</button>
          )}
          {period.status === 'gm_approved' && !summary.viewer_is_ceo && (
            <div className="muted">All managers approved. Waiting on CEO to finalize.</div>
          )}
          {period.status === 'paid' && <div className="muted">✓ Period finalized and paid.</div>}
        </div>
      )}

      {/* Manager approvals matrix (visible to CEO + managers) */}
      {approvals.length > 0 && (
        <div className="period-approvals">
          <h4>Manager Approvals</h4>
          <table className="data-table">
            <thead><tr><th>Manager</th><th>Status</th><th>When</th><th>Note</th></tr></thead>
            <tbody>
              {approvals.map(a => (
                <tr key={a.id}>
                  <td>{a.manager_name}</td>
                  <td><span className={`status-pill status-${a.status === 'approved' ? 'approved' : a.status === 'rejected' ? 'rejected' : 'pending'}`}>{a.status}</span></td>
                  <td className="muted">{a.approved_at || '—'}</td>
                  <td className="muted">{a.note || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="payroll-kpis">
        <div className="kpi-card kpi-regular">
          <div className="kpi-label">Regular</div>
          <div className="kpi-value">{hours(totals.regular)}h</div>
        </div>
        <div className="kpi-card kpi-ot">
          <div className="kpi-label">Overtime</div>
          <div className="kpi-value">{hours(totals.ot)}h</div>
        </div>
        <div className="kpi-card kpi-pto">
          <div className="kpi-label">Time Off</div>
          <div className="kpi-value">{hours(totals.pto)}h</div>
        </div>
        <div className="kpi-card kpi-total">
          <div className="kpi-label">Total</div>
          <div className="kpi-value">{hours(total)}h</div>
        </div>
      </div>

      <h3>Per Employee</h3>
      <table className="data-table">
        <thead>
          <tr>
            <th>Employee</th><th>Regular</th><th>OT</th><th>PTO</th><th>Total</th><th>Status</th>
          </tr>
        </thead>
        <tbody>
          {(summary?.summaries || []).map(s => {
            const sTotal = s.regular_seconds + s.ot_seconds + s.pto_seconds;
            return (
              <tr key={s.user_id}>
                <td>{s.full_name}</td>
                <td>{hours(s.regular_seconds)}h</td>
                <td>{hours(s.ot_seconds)}h</td>
                <td>{hours(s.pto_seconds)}h</td>
                <td><strong>{hours(sTotal)}h</strong></td>
                <td><span className={`status-pill status-${s.approval_status}`}>{s.approval_status}</span></td>
              </tr>
            );
          })}
          {(!summary || summary.summaries.length === 0) && <tr><td colSpan="6" className="empty">No data yet.</td></tr>}
        </tbody>
      </table>

      {accrual.length > 0 && (
        <>
          <h3>PTO Accrued This Period</h3>
          <table className="data-table">
            <thead>
              <tr><th>Employee</th><th>Hours Worked</th><th>Accrued</th><th>Capped</th><th>Balance After</th></tr>
            </thead>
            <tbody>
              {accrual.map(a => (
                <tr key={a.id}>
                  <td>{a.full_name}</td>
                  <td>{a.hours_worked.toFixed(2)}h</td>
                  <td>{a.hours_accrued.toFixed(3)}h</td>
                  <td>{a.hours_capped > 0 ? `${a.hours_capped.toFixed(3)}h (cap hit)` : '—'}</td>
                  <td><strong>{a.balance_after.toFixed(2)}h</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
